import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { useToast } from '@/shared/toast';
import { PageHeader } from '@/shared/PageHeader';

type Tab = 'works' | 'reports' | 'plan';

export function TrainingPage(): React.JSX.Element {
  usePageTitle('Addestramento');
  const [tab, setTab] = useState<Tab>('works');

  return (
    <div className="page-container">
      <PageHeader title="📋 Addestramento" />
      <div className="toolbar">
        <button onClick={() => setTab('works')} className={tab === 'works' ? 'btn-success' : 'btn-secondary'}>📋 Lavori da fare</button>
        <button onClick={() => setTab('reports')} className={tab === 'reports' ? 'btn-success' : 'btn-secondary'}>📣 Segnalazioni</button>
        <button onClick={() => setTab('plan')} className={tab === 'plan' ? 'btn-success' : 'btn-secondary'}>📝 Programma Addestramento</button>
      </div>
      {tab === 'works' && <WorksTab />}
      {tab === 'reports' && <ReportsTab />}
      {tab === 'plan' && <PlanTab />}
    </div>
  );
}

function WorksTab(): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({
    queryKey: ['training.works'],
    queryFn: async () => (await api.get('/training/works')).data,
  });
  // F15 #11: utenti del distaccamento per il tag @ (autocomplete).
  const { data: mentionUsers } = useQuery<{ nickname: string }[]>({
    queryKey: ['training.mentionable'],
    queryFn: async () => (await api.get('/training/mentionable-users')).data,
  });
  const [desc, setDesc] = useState('');
  const add = useMutation({
    mutationFn: async () => { await api.post('/training/works', { description: desc }); },
    onSuccess: () => { setDesc(''); qc.invalidateQueries({ queryKey: ['training.works'] }); toast.show('Lavoro aggiunto'); },
  });
  const del = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/training/works/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['training.works'] }),
  });
  const toggle = useMutation({
    mutationFn: async (w: { id: string; description: string; status: string }) => {
      const nextStatus = w.status === 'Done' ? 'Todo' : 'Done';
      await api.put(`/training/works/${w.id}`, { description: w.description, status: nextStatus });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['training.works'] }),
  });

  // Mention: frammento "@xxx" alla fine del testo digitato -> suggerimenti.
  const mentionToken = /@(\S*)$/;
  const mentionMatch = desc.match(mentionToken);
  const mentionFragment = mentionMatch ? mentionMatch[1].toLowerCase() : null;
  const suggestions = mentionFragment !== null
    ? (mentionUsers ?? []).filter(m => m.nickname.toLowerCase().includes(mentionFragment)).slice(0, 6)
    : [];
  function pickMention(nick: string): void {
    setDesc(d => d.replace(mentionToken, `@${nick} `));
  }

  return (
    <>
      <div className="card">
        <h3>Nuovo lavoro</h3>
        <div className="toolbar" style={{ alignItems: 'flex-start' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <input placeholder="Descrizione lavoro (usa @ per taggare un utente)" value={desc}
              onChange={e => setDesc(e.target.value)} style={{ width: '100%' }}
              onKeyDown={e => { if (e.key === 'Enter' && desc && suggestions.length === 0) add.mutate(); }} />
            {suggestions.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 2,
                background: '#fff', border: '1px solid #ccc', borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto',
              }}>
                {suggestions.map(s => (
                  <div key={s.nickname} onMouseDown={ev => { ev.preventDefault(); pickMention(s.nickname); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 14 }}>
                    👤 @{s.nickname}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => add.mutate()} disabled={!desc} className="btn-success">+ Aggiungi</button>
        </div>
      </div>
      <div className="table-wrapper">
        <table>
          <thead><tr><th style={{ width: 56 }}>Data</th><th>Descrizione</th><th>Autore</th><th>Stato</th><th></th></tr></thead>
          <tbody>
            {(data ?? []).map((w: { id: string; description: string; createdAt: string; createdByName: string; status: string }) => {
              const done = w.status === 'Done';
              const d = new Date(w.createdAt);
              return (
              <tr key={w.id}>
                {/* Data su 2 righe (gg/mm + anno) per ridurre la colonna. */}
                <td style={{ width: 56, fontSize: 11, lineHeight: 1.25, color: '#666', whiteSpace: 'nowrap' }}>
                  {d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}<br />{d.getFullYear()}
                </td>
                {/* Descrizione barrata se il lavoro e' fatto. */}
                <td style={done ? { textDecoration: 'line-through', color: '#888' } : undefined}>{w.description}</td>
                <td>{w.createdByName}</td>
                <td>
                  <button onClick={() => toggle.mutate(w)} title="Cambia stato"
                    style={{ background: done ? '#28a745' : '#dc3545',
                             color: 'white', padding: '4px 12px', border: 'none', borderRadius: 4,
                             cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    {done ? '✓ FATTO' : 'TODO'}
                  </button>
                </td>
                <td><button className="btn-delete" onClick={() => del.mutate(w.id)}>🗑</button></td>
              </tr>
              );
            })}
            {(data ?? []).length === 0 && <tr><td colSpan={5}><div className="empty-state">Nessun lavoro</div></td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

interface VehicleGroup {
  id: string; name: string;
  reports: Array<{ id: string; title: string; note: string; createdAt: string }>;
}
interface WarehouseGroup {
  id: string; name: string; category: string;
  entries: Array<{ id: string; type: 'segnalazione' | 'scadenza' | 'quantita'; note: string; createdAt: string }>;
}

function ReportsTab(): React.JSX.Element {
  const { data } = useQuery<{ vehicles: VehicleGroup[]; warehouse: WarehouseGroup[] }>({
    queryKey: ['training.reports'],
    queryFn: async () => (await api.get('/training/reports-aggregated')).data,
  });
  const vehicles = data?.vehicles ?? [];
  const warehouse = data?.warehouse ?? [];
  const total = vehicles.length + warehouse.length;

  if (total === 0) {
    return <div className="card"><h3>Segnalazioni aggregate</h3>
      <div className="empty-state">Nessuna segnalazione attiva. Tutto in ordine ✅</div></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* === MEZZI === */}
      <div className="card">
        <h3>🚒 Mezzi ({vehicles.length} {vehicles.length === 1 ? 'mezzo' : 'mezzi'} con segnalazioni)</h3>
        {vehicles.length === 0 ? (
          <div style={{ color: '#888', padding: 10 }}>Nessuna segnalazione sui mezzi.</div>
        ) : vehicles.map(v => (
          <details key={v.id} style={{ marginBottom: 8, background: '#f8f9fa', borderRadius: 6, padding: 8 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 700, padding: 6 }}>
              🚒 {v.name} <span style={{ color: '#666', fontWeight: 400 }}>({v.reports.length} segnalazioni)</span>
            </summary>
            <div style={{ marginTop: 8, paddingLeft: 8 }}>
              {v.reports.map(r => (
                <div key={r.id} style={{ background: '#fff3cd', border: '1px solid #ffeaa7',
                                          padding: 8, borderRadius: 4, marginBottom: 4, fontSize: 13 }}>
                  <div style={{ fontWeight: 600, color: '#856404' }}>{r.title}</div>
                  <div style={{ marginTop: 2 }}>"{r.note}"</div>
                  <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                    {new Date(r.createdAt).toLocaleString('it-IT')}
                  </div>
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>

      {/* === MAGAZZINO === */}
      <div className="card">
        <h3>📦 Magazzino ({warehouse.length} {warehouse.length === 1 ? 'articolo' : 'articoli'} con segnalazioni)</h3>
        {warehouse.length === 0 ? (
          <div style={{ color: '#888', padding: 10 }}>Nessuna segnalazione sul magazzino.</div>
        ) : warehouse.map(w => (
          <details key={w.id} style={{ marginBottom: 8, background: '#f8f9fa', borderRadius: 6, padding: 8 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 700, padding: 6 }}>
              📦 {w.name} <span style={{ color: '#888', fontWeight: 400, fontSize: 12 }}>({w.category})</span>
              <span style={{ color: '#666', fontWeight: 400 }}> · {w.entries.length} segnalazioni</span>
            </summary>
            <div style={{ marginTop: 8, paddingLeft: 8 }}>
              {w.entries.map((e, i) => {
                const bg = e.type === 'scadenza' ? '#f8d7da' : e.type === 'quantita' ? '#d1ecf1' : '#fff3cd';
                const border = e.type === 'scadenza' ? '#f5c6cb' : e.type === 'quantita' ? '#bee5eb' : '#ffeaa7';
                const tag = e.type === 'scadenza' ? '⏰ Scadenza' : e.type === 'quantita' ? '📊 Quantità' : '⚠️ Segnalazione';
                return (
                  <div key={`${e.id}-${i}`} style={{ background: bg, border: `1px solid ${border}`,
                                                      padding: 8, borderRadius: 4, marginBottom: 4, fontSize: 13 }}>
                    <div style={{ fontWeight: 600 }}>{tag}</div>
                    <div style={{ marginTop: 2 }}>{e.note}</div>
                  </div>
                );
              })}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

function PlanTab(): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({
    queryKey: ['training.plan'],
    queryFn: async () => (await api.get('/training/plan')).data,
  });
  const [content, setContent] = useState<string | null>(null);
  const current = content ?? data?.content ?? '';
  const save = useMutation({
    mutationFn: async () => { await api.put('/training/plan', { content: current }); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['training.plan'] }); toast.show('Piano salvato'); setContent(null); },
  });
  return (
    <div className="card">
      <h3>Programma Addestramento</h3>
      <p style={{ color: '#666', fontSize: 13 }}>
        Ultimo aggiornamento: {data?.updatedAt ? new Date(data.updatedAt).toLocaleString('it-IT') : '—'}
        {data?.updatedByName ? ` da ${data.updatedByName}` : ''}
      </p>
      <textarea value={current} onChange={e => setContent(e.target.value)}
        style={{ width: '100%', minHeight: 300 }} placeholder="Piano addestramento del distaccamento..." />
      <div style={{ marginTop: 10 }}>
        <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-success">💾 Salva piano</button>
      </div>
    </div>
  );
}
